/**
 * The list a select opens, drawn by the portal instead of by the system.
 *
 * The closed control was always ours - our own box with our own chevron - but
 * the list behind it belonged to the operating system, and a grey system menu
 * dropped into the middle of a dark page is the one part of a form which no
 * stylesheet can reach. So the native list is simply never opened.
 *
 * The real `<select>` stays in the page. It holds the value, it is what the
 * form submits, it is what the browser validates and refuses to submit, and
 * it is what a screen reader is handed. It is invisible and it takes no
 * clicks, but it keeps its place in the layout and its place in the tab
 * order - a control the browser cannot show is a control it will not report
 * an error on, and a form would then refuse to send with nothing said.
 *
 * Over it sit a button and a panel of our own, which is what everybody sees.
 * The panel is exactly as wide as the field it belongs to, and a list long
 * enough to be worth searching is given a search field of its own.
 */

import { foldAccents, query, queryAll } from "../core/dom";
import { text } from "../core/i18n";

/** Marks the control whose list is open. */
const OPEN = "is-open";

/** From how many options a list is given a search field of its own. */
const SEARCH_FROM = 12;

/** How long letters typed in a row are read as one word, in milliseconds. */
const TYPING_WINDOW = 900;

/** The tick beside the option which is the current answer. */
const CHECK = '<svg class="icon" width="14" height="14" aria-hidden="true"><use href="/static/assets/icons/icons.svg#check"></use></svg>';

/** The chevron of the closed control. */
const CHEVRON =
    '<svg class="icon select__chevron" width="16" height="16" aria-hidden="true">' +
    '<use href="/static/assets/icons/icons.svg#chevron-down"></use></svg>';

/** One control which has been given a list of its own. */
interface Control {
    readonly wrapper: HTMLElement;
    readonly close: (returnFocus: boolean) => void;
}

const controls: Control[] = [];

let wired = false;

/** Close every open list, except the one belonging to `keep`. */
export function closeAllSelects(keep?: HTMLElement): void {
    for (const control of controls) {
        if (control.wrapper !== keep) {
            control.close(false);
        }
    }
}

/** Return whether `select` is one this component leaves to the browser. */
export function isPlainSelect(select: HTMLSelectElement): boolean {
    return select.multiple || select.size > 1 || select.dataset["selectPlain"] !== undefined;
}

/**
 * Return whether `option` is the "nothing picked yet" row of a select.
 *
 * It is never offered in the list - picking it is not an answer - but its
 * wording is what the closed control says while nothing has been picked.
 */
function isPlaceholder(option: HTMLOptionElement): boolean {
    return option.value === "" && option.disabled;
}

let searches = 0;

/** Return an id for the search of `select`, unique on the page. */
export function searchIdFor(select: HTMLSelectElement): string {
    searches += 1;

    return `${select.id || select.name || "select"}-search-${searches}`;
}

/** Give one select a list of its own. */
export function enhanceSelect(select: HTMLSelectElement): void {
    if (isPlainSelect(select) || select.dataset["selectReady"] === "yes") {
        return;
    }

    select.dataset["selectReady"] = "yes";

    const wrapper = document.createElement("div");
    wrapper.className = "select";
    select.parentNode?.insertBefore(wrapper, select);
    wrapper.appendChild(select);

    const button = document.createElement("button");
    button.type = "button";
    button.className = "select__button";
    // The select underneath is the control: this is paint, and paint has no
    // business in the tab order or in what a screen reader is told about.
    button.tabIndex = -1;
    button.setAttribute("aria-hidden", "true");

    const value = document.createElement("span");
    value.className = "select__value";
    button.append(value);
    button.insertAdjacentHTML("beforeend", CHEVRON);

    const menu = document.createElement("div");
    menu.className = "select__menu";
    menu.hidden = true;

    const options = Array.from(select.options);
    const searchable = select.dataset["selectSearch"] !== undefined || options.length >= SEARCH_FROM;

    let search: HTMLInputElement | null = null;
    if (searchable) {
        const field = document.createElement("div");
        field.className = "select__search";
        search = document.createElement("input");
        // An id rather than a name: the search belongs to the list it
        // filters, and a name would send whatever was typed into it along
        // with the form.
        search.id = searchIdFor(select);
        search.type = "search";
        search.autocomplete = "off";
        search.placeholder = text("js.select.search", "Hľadať…");
        search.setAttribute("aria-label", text("js.select.search", "Hľadať…"));
        field.append(search);
        menu.append(field);
    }

    const list = document.createElement("div");
    list.className = "select__list";
    menu.append(list);

    // Two different empty lists: one where the search found nothing, and one
    // where there was nothing left to find because it has all been picked.
    const missing = text("js.select.nothing", "Nič sa nenašlo.");
    const exhausted = text("js.select.exhausted", "Vybrané je už všetko.");

    const nothing = document.createElement("div");
    nothing.className = "select__nothing";
    nothing.textContent = exhausted;
    nothing.hidden = true;
    menu.append(nothing);

    /** The rows of the list, in the order the select holds its options. */
    const rows = options
        .filter((option) => !isPlaceholder(option))
        .map((option) => {
            const row = document.createElement("div");
            row.className = "select__option";
            row.innerHTML = CHECK;

            // A row which stands for a person carries that person's face. The
            // option says where it is drawn rather than who it belongs to, so
            // a list of anything else can put a picture in front of its rows
            // the same way without this knowing what it is a picture of.
            const face = option.dataset["face"];
            if (face) {
                const picture = document.createElement("img");
                picture.className = "select__face";
                picture.src = face;
                picture.alt = "";
                picture.loading = "lazy";
                picture.setAttribute("aria-hidden", "true");
                row.append(picture);
            }

            const label = document.createElement("span");
            label.className = "select__label";
            label.textContent = (option.textContent ?? "").trim();
            row.append(label);

            if (option.disabled) {
                row.classList.add("is-locked");
            }

            list.append(row);

            return { option, row, haystack: foldAccents(label.textContent ?? "") };
        });

    wrapper.append(button, menu);

    let active = -1;
    let typed = "";
    let typedAt = 0;

    /** Return the rows the search has not filtered away. */
    const shown = (): number[] => rows.map((_, index) => index).filter((index) => !rows[index]?.row.hidden);

    /** Paint the closed control, the ticks and what is out of reach. */
    const render = (): void => {
        const chosen = select.selectedOptions[0] ?? null;
        value.textContent = chosen ? (chosen.textContent ?? "").trim() : "";
        // A row which answers with nothing reads as a prompt in the closed
        // control, whether it was written as one or is simply the "none" of
        // a field which may be left empty.
        value.classList.toggle("is-placeholder", chosen === null || chosen.value === "");

        for (const entry of rows) {
            entry.row.classList.toggle("is-chosen", entry.option.selected);
            // Read every time rather than once at the start: an option may be
            // switched off long after the list was drawn, the way a name
            // already picked as a recipient is.
            entry.row.classList.toggle("is-locked", entry.option.disabled);

            // Taking a row away is safe at any moment; putting one back waits
            // for the list to open, where it cannot undo a search in progress.
            if (entry.option.hidden) {
                entry.row.hidden = true;
            }
        }
    };

    /** Move the highlight onto `index`, or nowhere when it is out of range. */
    const highlight = (index: number): void => {
        active = index;

        for (const [position, entry] of rows.entries()) {
            entry.row.classList.toggle("is-active", position === index);
        }

        rows[index]?.row.scrollIntoView({ block: "nearest" });
    };

    /**
     * Put the list where the control is, and upwards when the room below has
     * run out.
     *
     * Measured against the window rather than against the control's own
     * corner, because the panel is fixed rather than absolute. A panel drawn
     * inside the control is clipped by anything above it which scrolls - a
     * dialog, a panel with a ceiling - and the list came out with a slice
     * missing off the bottom. Fixed, it belongs to the window and nothing on
     * the way to it can cut it.
     */
    const place = (): void => {
        menu.classList.remove("select__menu--above");

        const box = button.getBoundingClientRect();
        const below = window.innerHeight - box.bottom;
        const above = box.top;
        const gap = 6;

        menu.style.left = `${Math.round(box.left)}px`;
        menu.style.width = `${Math.round(box.width)}px`;

        // The room it has is the room on the side it opens towards, less the
        // gap, so a list longer than that scrolls instead of running off.
        const upwards = below < menu.offsetHeight + gap && above > below;

        menu.classList.toggle("select__menu--above", upwards);
        menu.style.maxHeight = `${Math.max(96, Math.round((upwards ? above : below) - gap * 2))}px`;

        if (upwards) {
            menu.style.top = "auto";
            menu.style.bottom = `${Math.round(window.innerHeight - box.top + gap)}px`;
        } else {
            menu.style.bottom = "auto";
            menu.style.top = `${Math.round(box.bottom + gap)}px`;
        }
    };

    /**
     * Show every row the control still offers.
     *
     * An option carrying `hidden` is not drawn at all, which is how a list
     * built by picking takes a name out of its own choices: the browser hides
     * it from the native list, and this hides it from the drawn one.
     */
    const unfilter = (): void => {
        for (const entry of rows) {
            entry.row.hidden = entry.option.hidden;
        }

        nothing.textContent = exhausted;
        nothing.hidden = shown().length > 0;
    };

    /** Narrow the list down to the rows matching what was typed. */
    const filter = (needle: string): void => {
        const wanted = foldAccents(needle.trim());

        for (const entry of rows) {
            entry.row.hidden = entry.option.hidden || (wanted !== "" && !entry.haystack.includes(wanted));
        }

        const left = shown();
        nothing.textContent = wanted === "" ? exhausted : missing;
        nothing.hidden = left.length > 0;
        highlight(left.includes(active) ? active : (left[0] ?? -1));
    };

    const close = (returnFocus: boolean): void => {
        if (!wrapper.classList.contains(OPEN)) {
            return;
        }

        wrapper.classList.remove(OPEN);
        menu.hidden = true;
        highlight(-1);

        window.removeEventListener("scroll", place, true);
        window.removeEventListener("resize", place);

        if (returnFocus) {
            select.focus();
        }
    };

    const open = (): void => {
        if (select.disabled || wrapper.classList.contains(OPEN)) {
            return;
        }

        closeAllSelects(wrapper);
        wrapper.classList.add(OPEN);
        menu.hidden = false;

        if (search) {
            search.value = "";
        }

        unfilter();
        highlight(rows.findIndex((entry) => entry.option.selected));
        place();
        search?.focus();

        // A panel which belongs to the window has to be told when the thing
        // it is pointing at moves, which nothing else would tell it.
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
    };

    /** Answer with the option of `index` and close the list. */
    const pick = (index: number): void => {
        const entry = rows[index];
        if (!entry || entry.option.disabled) {
            return;
        }

        const changed = !entry.option.selected;
        entry.option.selected = true;
        render();
        close(true);

        // The same pair a browser fires when somebody picks from the native
        // list, so a form which listens for one of them cannot tell the
        // difference - and one which listens for neither is not woken up.
        if (changed) {
            select.dispatchEvent(new Event("input", { bubbles: true }));
            select.dispatchEvent(new Event("change", { bubbles: true }));
        }
    };

    /** Move `by` rows down the list, skipping whatever the search hid. */
    const step = (by: number): void => {
        const left = shown();
        if (left.length === 0) {
            return;
        }

        const at = left.indexOf(active);
        const next = at === -1 ? (by > 0 ? 0 : left.length - 1) : (at + by + left.length) % left.length;

        highlight(left[next] ?? -1);
    };

    /** Jump to the row starting with the letters typed in a row. */
    const jump = (letter: string): void => {
        const now = Date.now();
        typed = now - typedAt > TYPING_WINDOW ? letter : typed + letter;
        typedAt = now;

        const start = rows.findIndex((entry) => entry.haystack.startsWith(foldAccents(typed)));
        if (start !== -1) {
            highlight(start);
        }
    };

    const onKey = (event: KeyboardEvent): void => {
        const isOpen = wrapper.classList.contains(OPEN);

        if (!isOpen) {
            // Enter is left alone: on a form, Enter sends it, and a control
            // which swallowed that would be the one field behaving oddly.
            if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === " " || event.key === "F4") {
                event.preventDefault();
                open();
            }

            return;
        }

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                step(1);
                break;
            case "ArrowUp":
                event.preventDefault();
                step(-1);
                break;
            case "Home":
                event.preventDefault();
                highlight(shown()[0] ?? -1);
                break;
            case "End": {
                event.preventDefault();
                const left = shown();
                highlight(left[left.length - 1] ?? -1);
                break;
            }
            case "Enter":
                // Neither sends the form nor lets a dialog answer for it.
                event.preventDefault();
                event.stopPropagation();
                pick(active);
                break;
            case " ":
                // A space is a letter while there is a search field to type
                // it into, and the answer everywhere else.
                if (!search) {
                    event.preventDefault();
                    pick(active);
                }

                break;
            case "Escape":
                // Stopped here, or the dialog around the form closes with it.
                event.preventDefault();
                event.stopPropagation();
                close(true);
                break;
            case "Tab":
                close(false);
                break;
            default:
                if (!search && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    event.preventDefault();
                    jump(event.key);
                }
        }
    };

    button.addEventListener("mousedown", (event: MouseEvent) => {
        // Before the focus moves, so that a second press closes the list
        // instead of closing and opening it again.
        event.preventDefault();

        if (wrapper.classList.contains(OPEN)) {
            close(true);

            return;
        }

        select.focus();
        open();
    });

    // Without this the press moves the focus out of the panel, the panel
    // closes on the way, and the click it was about never reaches a row.
    list.addEventListener("mousedown", (event: MouseEvent) => event.preventDefault());

    list.addEventListener("click", (event: MouseEvent) => {
        const row = (event.target as HTMLElement | null)?.closest(".select__option");
        if (row) {
            pick(rows.findIndex((entry) => entry.row === row));
        }
    });

    list.addEventListener("mousemove", (event: MouseEvent) => {
        const row = (event.target as HTMLElement | null)?.closest(".select__option");
        if (row) {
            highlight(rows.findIndex((entry) => entry.row === row));
        }
    });

    select.addEventListener("keydown", onKey);
    search?.addEventListener("keydown", onKey);
    search?.addEventListener("input", () => filter(search.value));

    // Whatever moved the value - our list, a script, the keyboard on the
    // focused select - the paint follows the control rather than the click.
    select.addEventListener("change", render);

    // `reset()` puts every field back without telling any of them, and the
    // event arrives before the values do - hence the trip through the queue.
    select.form?.addEventListener("reset", () => window.setTimeout(render, 0));

    wrapper.addEventListener("focusout", (event: FocusEvent) => {
        const next = event.relatedTarget;
        if (!(next instanceof Node) || !wrapper.contains(next)) {
            close(false);
        }
    });

    /** Follow the control into and out of being disabled. */
    const syncDisabled = (): void => {
        wrapper.classList.toggle("is-disabled", select.disabled);

        if (select.disabled) {
            close(false);
        }
    };

    new MutationObserver(syncDisabled).observe(select, { attributes: true, attributeFilter: ["disabled"] });

    syncDisabled();
    render();
    controls.push({ wrapper, close });
}

/** Give every select of the page a list of its own. */
export function initSelects(): void {
    for (const select of queryAll<HTMLSelectElement>("select")) {
        enhanceSelect(select);
    }

    if (controls.length === 0 || wired) {
        return;
    }

    wired = true;

    document.addEventListener("mousedown", (event: Event) => {
        const target = event.target;
        if (target instanceof Node && query<HTMLElement>(".select.is-open")?.contains(target)) {
            return;
        }

        closeAllSelects();
    });
}
